import React, { useState } from 'react';
import PageLayout from '../layouts/PageLayout';
import '../styles/pages/index.css';
import Input from '../components/Input';
import Button from '../components/Button';
import { useTheme } from '../hooks/useTheme';
import PlusSubmitIcon from '../Icons/PlusSubmitIcon';
import { useRouteIsLoading } from 'context/RouteIsLoadingContext';
import { useEffect } from 'react';
import { useSanityMetaTags } from '../hooks/useSanityMetaTags';

const Contact = () => {
  const { isDarkMode } = useTheme();
  const { setIsLoading: setRouteContextIsLoading } = useRouteIsLoading();
  useSanityMetaTags('contact');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    message: '',
  });
  const [isLoading, setIsLoading] = useState(false);
  const [validationErrors, setValidationErrors] = useState({
    name: false,
    email: false,
    company: false,
    phone: false,
  });

  useEffect(() => {
    setRouteContextIsLoading(false);
  }, [setRouteContextIsLoading]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (validationErrors[name]) {
      setValidationErrors((prev) => ({
        ...prev,
        [name]: false,
      }));
    }
  };

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const normalizePhone = (raw) => {
    if (!raw) return '';
    const trimmed = raw.trim();
    const withPlus = trimmed.startsWith('00')
      ? `+${trimmed.slice(2)}`
      : trimmed;
    const cleaned = withPlus.replace(/(?!^)[^\d]/g, '');
    if (cleaned.startsWith('+')) return cleaned;
    if (/^\d+$/.test(cleaned)) return `+${cleaned}`;
    return cleaned;
  };

  const isE164 = (value) => {
    return /^\+[1-9]\d{7,14}$/.test(value);
  };

  const validatePhone = (phone) => {
    if (!phone.trim()) return true;
    const normalized = normalizePhone(phone);
    return isE164(normalized);
  };

  const validateForm = () => {
    const errors = {
      name: !formData.name.trim(),
      email: !formData.email.trim() || !validateEmail(formData.email),
      company: !formData.company.trim(),
      phone: !validatePhone(formData.phone),
    };

    setValidationErrors(errors);
    return !Object.values(errors).some((error) => error);
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    const myHeaders = new Headers();
    myHeaders.append('Content-Type', 'application/json');
    myHeaders.append(
      'Authorization',
      'Bearer skNeRLxHT0sGe9HaThzwVRYv8MMzFqQq9GlV9GhYD7LKKqLtMFJpT1AgZ0bYAa460uMMIjLs8cqnBjOMUxamTflqRDuBbjU4eisDp8TWTr1zpsOS5JjxupZns4h6xHZ3kvXiYqnDacG0ymlMm19SlIWH9AlGIXM7MUdOHY167ln0Pz4XAPrw',
    );

    const raw = JSON.stringify({
      mutations: [
        {
          create: {
            ...formData,
            _id: Math.floor(Date.now() / 1000).toString(),
            _type: 'contact',
            submissionDate: new Date().toISOString(),
          },
        },
      ],
    });

    const requestOptions = {
      method: 'POST',
      headers: myHeaders,
      body: raw,
      redirect: 'follow',
    };

    fetch(
      'https://kxxtoumf.api.sanity.io/v2025-05-12/data/mutate/production',
      requestOptions,
    )
      .then((response) => response.text())
      .then((result) => {
        setFormData({
          name: '',
          email: '',
          company: '',
          phone: '',
          message: '',
        });
        setValidationErrors({
          name: false,
          email: false,
          company: false,
          phone: false,
        });
      })
      .catch((error) => console.error(error))
      .finally(() => setIsLoading(false));
  };

  return (
    <PageLayout>
      <div className='page-wrapper contact-page-wrapper'>
        <div className='flex contact-page'>
          <div className='flex'>
            <p className='balanced-text uppercase full-width sec-nd-hp-p font-primary'>
              <span>Wie kann ich </span>
              <span className='font-secondary non-active'>sie Erreichen?</span>
            </p>
          </div>
          <form
            className={`flex contact-form ${!isDarkMode ? 'light-form' : ''}`}
            onSubmit={handleSubmit}
          >
            <Input
              placeholder='Name*'
              name='name'
              value={formData.name}
              onChange={handleInputChange}
              className={`${validationErrors.name ? 'validation-error' : ''} ${
                isLoading ? 'loading' : ''
              }`}
            />
            <Input
              placeholder='E-Mail*'
              name='email'
              value={formData.email}
              onChange={handleInputChange}
              className={`${validationErrors.email ? 'validation-error' : ''} ${
                isLoading ? 'loading' : ''
              }`}
            />
            <Input
              placeholder='Firmenname*'
              name='company'
              value={formData.company}
              onChange={handleInputChange}
              className={`${
                validationErrors.company ? 'validation-error' : ''
              } ${isLoading ? 'loading' : ''}`}
            />
            <Input
              type='tel'
              inputMode='tel'
              autoComplete='tel'
              placeholder='Phone (e.g., +49 15123456789)'
              name='phone'
              value={formData.phone}
              onChange={handleInputChange}
              onBlur={(e) => {
                const normalized = normalizePhone(e.target.value);
                setFormData((prev) => ({ ...prev, phone: normalized }));
              }}
              pattern='^\+[1-9]\d{7,14}$'
              maxLength='16'
              title='Enter a valid number in E.164 format, e.g., +4915123456789'
              className={`${validationErrors.phone ? 'validation-error' : ''} ${
                isLoading ? 'loading' : ''
              }`}
            />
            <textarea
              placeholder='Hinterlassen Sie eine Nachricht'
              name='message'
              value={formData.message}
              onChange={handleInputChange}
              maxLength='2000'
              className={isLoading ? 'loading' : ''}
            />
            <Button
              className='flex justify-center'
              variant={isDarkMode ? '' : 'dark'}
              showIcon={true}
              customIcon={<PlusSubmitIcon isLoading={isLoading} />}
              type='submit'
            >
              Kontakt teilen
            </Button>
          </form>
        </div>
      </div>
    </PageLayout>
  );
};

export default Contact;
